import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { isMobileSafari } from 'react-device-detect';
import { useSessionstorageState } from 'rooks';
import shallow from 'zustand/shallow';

import MuxVideo from '@mux/mux-video-react';

import { VideoData } from 'lib/pages/talks/slug/[...slug].props';

import useVideoPlayerStore from './store';

import * as styles from './InternalPlayer/Player.styles';

import acceptedTalks from 'components/pages/dubbing/slug/accepted-talks.json';
import { useVWOCustomConversionTracking } from './InternalPlayer/hooks/useCustomVWOTracking';
import { useFetchSubtitles } from './InternalPlayer/hooks/useFetchSubtitles';
import { useKeyboardControls } from './InternalPlayer/hooks/useKeyboardControls';
import { useLanguageSettings } from './InternalPlayer/hooks/useLanguageSettings';
import { useMixpanelData } from './InternalPlayer/hooks/useMixpanelData';
import { usePlayerEvents } from './InternalPlayer/hooks/usePlayerEvents';
import { useSetup } from './InternalPlayer/hooks/usePlayerSetup';

import useDubbingSurvey from './InternalPlayer/hooks/useDubbingSurvey';
import {
  getUserActivationState,
  isUserActivationSupported
} from './InternalPlayer/utils';

const Controls = dynamic(() => import('./InternalPlayer/Controls/Controls'), {
  ssr: false
});
const SubtitleCredits = dynamic(
  () => import('./InternalPlayer/Subtitles/subtitle-credits'),
  {
    ssr: false
  }
);
const SubtitleCue = dynamic(
  () => import('./InternalPlayer/Subtitles/subtitle-cue'),
  { ssr: false }
);

export type Language = {
  endonym: string;
  ianaCode: string;
  isRtl: boolean;
  languageCode: string;
  languageName: string;
};

type VideoPlayerProps = {
  video: VideoData;
  debug?: boolean;
  hovered?: boolean;
};

function VideoPlayer({
  video,
  debug,
  hovered
}: VideoPlayerProps): React.ReactNode {
  const router = useRouter();
  const {
    isMobile,
    shouldAutoplay,
    mixpanelDistinctId,
    userId,
    playerTalkData,
    service,
    muted,
    startTime
  } = useSetup(video);

  const userActivationSupported = isUserActivationSupported();
  const userHasBeenActive = getUserActivationState();
  const { surveyDismissed } = useDubbingSurvey();

  const [player, setPlayer] = useState<HTMLVideoElement | null>(null);

  const [playerReady, setPlayerReady] = useState(false);
  // For use when the dubbing language is changed to check if the player is ready to play
  const [isReadyToPlay, setIsReadyToPlay] = useState(false);

  const [isMuted, setIsMuted] = useSessionstorageState(
    'muted_state',
    !userActivationSupported
  );

  const isMutedParam = Boolean(router.query.muted);
  const isMutedParamValue = router.query.muted === 'true';

  const {
    contentState,
    playbackMode,
    roundedTime: currentTime,
    onBufferState,
    onDuration,
    onError,
    onPlaybackMode,
    onReady,
    requestedCurrentTime,
    onPause,
    onStreamDuration,
    onStreamHost,
    onTalkComplete,
    onPlay,
    ready,
    requestMute,
    subtitleCues,
    dubbingLang,
    dubbingPreviousTime,
    pauseCalled,
    playCalled,
    callPause,
    callPlay,
    onDubbingLanguage,
    onChangeDubbingLang,
    settingsOpen
  } = useVideoPlayerStore(
    state => ({
      ...state
    }),
    shallow
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mainContentSrc: any = useMemo(() => {
    const data = acceptedTalks.find(talk => talk.id === video.id);

    return data.playerData;
  }, [video.id]);

  useEffect(() => {
    if (pauseCalled) {
      callPause(null);
      player.pause();
    }
    if (playCalled) {
      callPlay(null);
      player.play();
    }
  }, [pauseCalled, playCalled]);

  useEffect(() => {
    if (player) {
      player.currentTime = dubbingPreviousTime;

      setIsReadyToPlay(false);
    }
  }, [dubbingLang]);

  const internalPlayer = service === 'internal';

  const playerRef = useCallback((node: HTMLVideoElement | null) => {
    if (!node) return setPlayer(null);

    setPlayer(node);
  }, []);

  const getInitialMutedState = () => {
    switch (true) {
      case isMutedParam:
        return 'isMutedParam';
      case userActivationSupported:
        return 'hasUserActivation';
      case isMuted:
        return 'isMuted';
      default:
        return 'default';
    }
  };

  const setMutedState = useCallback(
    (muted: boolean) => {
      if (!player) return;

      player.muted = muted;
      requestMute(muted, false);
      setIsMuted(muted);
    },
    [player]
  );

  const handleBufferStart = useCallback(() => {
    if (!player.paused && !player.seeking) {
      onBufferState(true);
    }
  }, [player]);

  const handleBufferEnd = useCallback(() => onBufferState(false), []);

  const handlePlayerReady = useCallback(() => {
    setPlayerReady(true);
    setIsReadyToPlay(true);
    onChangeDubbingLang(false);
  }, []);

  const handleEnded = useCallback(() => {
    onPlaybackMode('ended');
    onTalkComplete();
  }, [onPlaybackMode, onTalkComplete]);

  // Determine if controls, subtitles, and ads should be displayed
  const displayControls = useMemo(
    () => !!playerReady && playbackMode !== 'ended',
    [playerReady, playbackMode]
  );

  const displaySubtitles = useMemo(
    () => router.query.subtitle && subtitleCues.length > 0,
    [router.query.subtitle, subtitleCues]
  );

  // Determine classes for animations and transitions
  const animateVisibleClasses = useMemo(
    () =>
      cx('transition-opacity duration-300 ease-in-out', {
        'opacity-0 delay-1000': !hovered && !isMobile,
        'opacity-100': hovered && !isMobile
      }),
    [hovered, isMobile]
  );

  const animateSubtitlePositionClasses = useMemo(
    () =>
      cx('transition-transform duration-300 ease-in-out', {
        'translate-y-5 delay-1000': !hovered && !isMobile,
        'translate-y-0': hovered && !isMobile
      }),
    [hovered, isMobile]
  );

  // Keyboard controls for the player
  const { togglePlay } = useKeyboardControls({
    player
  });
  // Fetch subtitles for the video and transcript and set the language
  useFetchSubtitles(playerTalkData);
  // Set the player's playback rate and muted state and update the subtitle cue
  usePlayerEvents(player, displayControls);
  useVWOCustomConversionTracking(currentTime, video.duration);
  // Set the language settings for the player
  useLanguageSettings(playerTalkData);
  // Setup the mixpanel tracking data
  useMixpanelData(video);

  useEffect(() => {
    // Reset player and playback time when playback mode changes to 'replayed'
    if (playbackMode === 'replayed' && player) {
      player.currentTime = 0;
      player.play().catch(error => {
        console.error('Play attempt failed:', error);
      });
    }
  }, [currentTime, onPlay, playbackMode, player, video.duration]);

  useEffect(() => {
    // Set player ready when ad finishes loading
    if (playerReady && internalPlayer) {
      const mainContentDuration = player?.duration;
      onReady(true);

      onStreamDuration(player?.duration);
      onDuration(Math.floor(mainContentDuration));
    }
  }, [internalPlayer, playerReady, contentState, player?.duration]);

  useEffect(() => {
    if (player && ready) {
      onStreamHost('hls.ted.com');
    }
  }, [mainContentSrc, player, ready]);

  useEffect(() => {
    if (player && internalPlayer) {
      player.addEventListener('canplay', handlePlayerReady);
      player.addEventListener('waiting', handleBufferStart);
      player.addEventListener('playing', handleBufferEnd);
      player.addEventListener('canplay', handleBufferEnd);

      return () => {
        player.removeEventListener('canplay', handlePlayerReady);
        player.removeEventListener('waiting', handleBufferStart);
        player.removeEventListener('playing', handleBufferEnd);
        player.removeEventListener('canplay', handleBufferEnd);
      };
    }
  }, [player, internalPlayer, handlePlayerReady]);

  useEffect(() => {
    if (player && requestedCurrentTime !== null) {
      // Adjust the player.currentTime by adding prerollDuration to align with the actual timeline including the ad
      const adjustedTime = requestedCurrentTime;
      player.currentTime = adjustedTime;
    }
  }, [requestedCurrentTime, player, contentState]);

  useEffect(() => {
    if (
      player &&
      internalPlayer &&
      playerReady &&
      mainContentSrc &&
      isReadyToPlay
    ) {
      const initialMutedState = getInitialMutedState();

      switch (initialMutedState) {
        case 'isMutedParam':
          setMutedState(isMutedParamValue);
          break;
        case 'hasUserActivation':
          if (userActivationSupported && !userHasBeenActive) {
            setMutedState(true);
          } else {
            setMutedState(isMuted);
          }
          break;
        case 'isMuted':
          setMutedState(true);
          break;
        default:
          setMutedState(false);
      }

      if (playbackMode !== 'paused') {
        onPlay();
        player.play().catch(error => {
          console.error('Play attempt failed:', error);
        });
      }
    }
  }, [playerReady, mainContentSrc, dubbingLang, isReadyToPlay]);

  // Needed because could have videos without the lang selected,
  // so we put it back to english the dubbing
  const videoUrl = () => {
    if (mainContentSrc.resources.hls.stream[dubbingLang]) {
      return mainContentSrc.resources.hls.stream[dubbingLang];
    }
    onDubbingLanguage('en');
    router.push({ query: { ...router.query, audio: 'en' } });
    return mainContentSrc.resources.hls.stream.en;
  };

  if (!playerTalkData) {
    return null;
  }

  return (
    <>
      <MuxVideo
        id="video"
        ref={playerRef}
        src={videoUrl()}
        slot="media"
        type="hls"
        debug={debug}
        preload="auto"
        muted={muted}
        style={styles.topChromeStyles}
        onPlay={onPlay}
        onClick={togglePlay}
        onPause={onPause}
        onError={onError}
        onEnded={handleEnded}
        autoPlay
        playsInline
        webkit-playsinline
        controls={false}
        controlsList="nodownload"
        crossOrigin="anonymous"
        envKey={process.env.NEXT_PUBLIC_MUX_ENV_KEY}
        metadata={{
          player_name: 'Zenith HLS Mux Player (Internal)',
          page_type: 'watchpage',

          player_autoplay_on: shouldAutoplay,
          player_init_time: startTime || 0,

          video_id: video?.id,
          video_title: video?.slug,
          video_duration: video?.duration,
          video_language_code: router.query?.lang as string,
          video_encoding_variant:
            playerTalkData?.resources?.hls?.stream.indexOf('manifest.m3u8') > -1
              ? 'consus'
              : 'symfony',
          video_stream_type: 'on-demand',

          viewer_user_id: userId,
          sub_property_id: 'zenith',
          custom_1: mixpanelDistinctId || 'unknown'
        }}
        {...(startTime && { startTime })}
        {...(isMobileSafari && { autoPlay: 'any' })}
      />

      {displayControls && (
        <div
          className={cx(
            `pointer-events-none absolute z-40 size-full lg-tui:bottom-0 lg-tui:h-auto ${animateVisibleClasses}`,
            {
              'opacity-100': !surveyDismissed || settingsOpen
            }
          )}
        >
          <Controls playerTalkData={playerTalkData} />
        </div>
      )}

      {displaySubtitles && (
        <div className="pointer-events-none size-full opacity-100">
          <div
            className={`absolute top-0 hidden w-full md:block ${animateVisibleClasses}`}
          >
            <SubtitleCredits />
          </div>
          <SubtitleCue
            className={cx(
              `absolute bottom-5 w-full md-tui:bottom-16 lg-tui:bottom-18`,
              animateSubtitlePositionClasses
            )}
          />
        </div>
      )}
    </>
  );
}

export default memo(VideoPlayer);
